import {
  useHarnessCredentials,
  useRevokeHarnessCredential,
} from "@/api/harnessCredentials";
import {
  chipButtonClassName,
  chipDangerButtonClassName,
  GroupLabel,
  LcBadge,
  NESTED_DIALOG_Z,
  PaneBody,
  Row,
  smButtonClassName,
} from "@/components/AdminPanel/Organization/components/manage-pane-primitives";
import {
  ConnectProviderDialog,
  KIND_LABELS,
  MODEL_PROVIDERS,
  ModelProvider,
} from "@/components/harness/ConnectProviderDialog";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/utils";
import { HarnessCredential } from "@cohesive/api-client";
import { Spinner } from "@cohesive/ui-components";
import { formatDistanceToNow } from "date-fns";
import React, { useMemo, useState } from "react";
import toast from "react-hot-toast";

/**
 * Model providers — whose provider bill an agent turn is spent against.
 *
 * The roster's shape, one row per provider rather than one per credential:
 * only one credential is ever in force for a provider, and which one is the
 * point (yours beats your organization's, and both beat Cohesive's). A row
 * therefore reads as an answer to "who pays for this", with the connect and
 * disconnect verbs hung off it.
 */

/**
 * The row's second line: whether the subscription is connected, and so whether chats on its agent
 * can run. Nothing stands in for it — a subscription chat never moves to Cohesive's key — so an
 * unconnected or rejected one says which chats it holds up. Exported because that is the pane's
 * whole point, worth asserting without a render.
 */
export function describeCredential(
  provider: Pick<ModelProvider, "agentLabel">,
  credential?: HarnessCredential,
): string {
  if (credential?.status === "error") {
    // The trailing period goes so a provider-supplied sentence joins cleanly.
    const reason = (
      credential.status_detail ?? "The provider rejected this credential"
    ).replace(/\.$/, "");
    return `${reason} — reconnect it to run ${provider.agentLabel} chats`;
  }
  if (credential) {
    return `${KIND_LABELS[credential.kind]} ${nameAccount(credential)} · connected ${formatDistanceToNow(
      new Date(credential.connected_at),
      { addSuffix: true },
    )}`;
  }
  return `Not connected · needed for ${provider.agentLabel} chats`;
}

/**
 * Whose account this is, in the most recognisable form the provider gave us: the email, plus the
 * workspace where the provider names one. A credential connected before we collected either — and
 * any API key, which is issued to nobody in particular — falls back to the token's last four.
 *
 * Anthropic auto-names a personal subscription's organization after the account, so a workspace
 * that only restates the email is dropped rather than printed twice. The stored value stays.
 */
function nameAccount(credential: HarnessCredential): string {
  const { account_email: email, account_workspace: workspace } = credential;
  if (!email) return `ending ${credential.tail}`;
  if (!workspace || workspace === `${email}'s Organization`) return email;
  return `${email} · ${workspace}`;
}

function RevokeDialog({
  credential,
  provider,
  open,
  onOpenChange,
}: {
  credential: HarnessCredential;
  provider: ModelProvider;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { mutateAsync: revoke, isPending } = useRevokeHarnessCredential();
  const providerName = provider.label;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => !isPending && onOpenChange(next)}
    >
      <DialogContent
        className={cn("sm:max-w-[425px]", NESTED_DIALOG_Z)}
        overlayClassName={NESTED_DIALOG_Z}
      >
        <DialogHeader>
          <DialogTitle>Disconnect {providerName}</DialogTitle>
          <DialogDescription>
            Sandboxes already running stop using it within moments, and{" "}
            {provider.agentLabel} chats and automations stop until it is
            connected again — they never move to Cohesive. This cannot be
            undone; reconnecting means signing in again.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            variant="outline"
            disabled={isPending}
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            className="bg-spectrum-red text-paper hover:bg-[color-mix(in_oklab,var(--spectrum-red)_88%,var(--ink))]"
            disabled={isPending}
            isLoading={isPending}
            onClick={async () => {
              try {
                await revoke(credential);
                onOpenChange(false);
                toast.success(`${providerName} disconnected.`);
              } catch {
                toast.error(
                  `Failed to disconnect ${providerName}. Please try again.`,
                );
              }
            }}
          >
            Disconnect
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const ModelProviders: React.FC = () => {
  const { data: credentials, isLoading, isError } = useHarnessCredentials();
  const [connecting, setConnecting] = useState<ModelProvider | null>(null);
  const [revoking, setRevoking] = useState<{
    credential: HarnessCredential;
    provider: ModelProvider;
  } | null>(null);

  /** The user endpoint returns both scopes; a chat on a subscription agent spends only the user's own. */
  const byScope = useMemo(() => {
    const map = new Map<string, HarnessCredential>();
    for (const credential of credentials ?? []) {
      map.set(`${credential.scope}:${credential.provider}`, credential);
    }
    return map;
  }, [credentials]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Spinner />
      </div>
    );
  }

  // Without this a failed list renders every subscription as not connected — the one thing this
  // pane exists to state.
  if (isError) {
    return (
      <div className="flex items-center justify-center py-16 text-sm text-n-50">
        Could not load model providers. Refresh to try again.
      </div>
    );
  }

  const providerRow = (provider: ModelProvider) => {
    const Icon = provider.icon;
    const credential = byScope.get(`user:${provider.id}`);
    const hasError = credential?.status === "error";

    return (
      <Row
        key={provider.id}
        tile={<Icon className="size-5" />}
        name={provider.label}
        meta={describeCredential(provider, credential)}
      >
        {hasError && <LcBadge tone="blocked">Error</LcBadge>}
        {credential ? (
          <>
            <Button
              variant="ghost"
              size="sm"
              className={chipButtonClassName}
              onClick={() => setConnecting(provider)}
            >
              Replace
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className={chipDangerButtonClassName}
              onClick={() => setRevoking({ credential, provider })}
            >
              Disconnect
            </Button>
          </>
        ) : (
          <Button
            variant="secondary"
            size="sm"
            className={cn("shrink-0", smButtonClassName)}
            onClick={() => setConnecting(provider)}
          >
            Connect
          </Button>
        )}
      </Row>
    );
  };

  return (
    <PaneBody>
      <section className="flex flex-col gap-0.5">
        <GroupLabel>Your subscriptions</GroupLabel>
        {MODEL_PROVIDERS.map(providerRow)}
      </section>
      <p className="px-2.5 text-sm leading-[21px] text-n-50">
        A connected subscription only runs the chats you start on Claude Code or
        ChatGPT. Those chats never move to Cohesive, and Cohesive chats never
        use your subscription.
      </p>

      {connecting && (
        <ConnectProviderDialog
          open
          provider={connecting}
          onOpenChange={(open) => {
            if (!open) setConnecting(null);
          }}
        />
      )}

      {revoking && (
        <RevokeDialog
          open
          credential={revoking.credential}
          provider={revoking.provider}
          onOpenChange={(open) => {
            if (!open) setRevoking(null);
          }}
        />
      )}
    </PaneBody>
  );
};

export default ModelProviders;
